// apps/client_diy_webapp/src/pages/qualitative-surveys/components/Incentives.js
import React, { useEffect, useState } from "react";
import { RiPencilLine } from "react-icons/ri";
import { useGlobalContext } from "../../../Contexts/GlobalContext";
import RadioCard from "./RadioCard";
import SaveBar from "./Savebar";
import { currencies, EDITABLE_STATUSES } from "../qualitativeUtils";
import { useProjectUpdate } from "../useProjectUpdate";


const MANUAL_INCENTIVE_TYPES = [
    { value: "visa_gift_card", label: "Visa gift card" },
    { value: "tango_gift_card", label: "Tango gift card" },
    { value: "amazon_gift_card", label: "Amazon gift card" },
    { value: "other", label: "Other" },
];

const SELECT_STYLE = {
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 12px center",
    backgroundSize: "12px",
    paddingRight: "36px",
};

export default function Incentives({ projectDetails }) {
    const { user } = useGlobalContext();
    const organizationID = user?.organization?._id;
    const projectID = projectDetails?._id;
    const isEditable = EDITABLE_STATUSES?.includes(projectDetails?.status);

    // currency is an object {label, value, symbol}
    const [currencyValue, setCurrencyValue] = useState(
        projectDetails?.incentives?.currency?.value ?? "USD"
    );
    const [amount, setAmount] = useState(
        projectDetails?.incentives?.incentiveAmount ?? 0
    );
    const [distributionMethod, setDistributionMethod] = useState(
        projectDetails?.incentives?.incentiveDistributionMethod ?? ""
    );
    const [manualType, setManualType] = useState(
        projectDetails?.incentives?.manualIncentiveType ?? ""
    );
    const [manualOtherText, setManualOtherText] = useState(
        projectDetails?.incentives?.manualIncentiveOtherText ?? ""
    );

    useEffect(() => {
        setCurrencyValue(projectDetails?.incentives?.currency?.value ?? "USD");
        setAmount(projectDetails?.incentives?.incentiveAmount ?? 0);
        setDistributionMethod(projectDetails?.incentives?.incentiveDistributionMethod ?? "");
        setManualType(projectDetails?.incentives?.manualIncentiveType ?? "");
        setManualOtherText(projectDetails?.incentives?.manualIncentiveOtherText ?? "");
    }, [projectDetails]);

    const { save, isSaving, saveError, saveSuccess } = useProjectUpdate(organizationID, projectID);

    const handleSave = () => {
        // Reconstruct currency object from selected value
        const currencyObj = currencies.find((c) => c.value === currencyValue) ?? {
            label: "US Dollar", value: "USD", symbol: "$",
        };
        save({
            incentives: {
                incentiveAmount: Number(amount),
                currency: currencyObj,
                incentiveDistributionMethod: distributionMethod || null,
                manualIncentiveType: manualType || null,
                manualIncentiveOtherText: manualOtherText || null,
            },
        });
    };

    return (
        <div className="space-y-6">
            {/* ── Incentive Amount ──────────────────────────────────────── */}
            <div className="space-y-3 bg-white p-3 rounded-xl">
                <div className="space-y-1">
                    <h1 className="font-bold text-md">Incentive Amount</h1>
                    <p className="text-gray-500 text-xs">
                        Define the incentive offered to each participant upon session completion.
                    </p>
                </div>

                <div className="space-y-3 border border-gray-300 rounded-xl p-3">
                    {/* Currency */}
                    <div className="space-y-1.5">
                        <p className="text-gray-600 text-sm font-medium">
                            Currency <span className="text-red-500">*</span>
                        </p>
                        <select
                            className="border border-gray-300 rounded-md py-2.5 px-[14px] text-[#667085] text-[14px]
                                focus:outline-none focus:ring-1 focus:ring-[#0226BE] transition-all appearance-none
                                cursor-pointer hover:border-gray-400 bg-white w-full
                                disabled:bg-gray-50 disabled:cursor-not-allowed"
                            style={SELECT_STYLE}
                            value={currencyValue}
                            onChange={(e) => isEditable && setCurrencyValue(e.target.value)}
                            disabled={!isEditable}
                            name="currency"
                        >
                            <option value="">Select the currency</option>
                            {currencies.map((c) => (
                                <option key={c.value} value={c.value}>
                                    {c.label} ({c.symbol})
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Amount */}
                    <div className="space-y-1.5">
                        <p className="text-gray-600 text-sm font-medium">
                            Amount <span className="text-red-500">*</span>
                        </p>
                        <div className="border rounded-lg px-[14px] py-2.5 flex items-center gap-2 bg-white">
                            {/* Show currency symbol prefix */}
                            <span className="text-gray-400 text-sm flex-shrink-0">
                                {currencies.find((c) => c.value === currencyValue)?.symbol ?? "$"}
                            </span>
                            <input
                                type="number"
                                className="flex-1 text-sm placeholder:text-sm focus:outline-none bg-transparent
                                    disabled:text-gray-500 disabled:cursor-not-allowed"
                                placeholder="0"
                                min={0}
                                step="any"
                                value={amount}
                                onChange={(e) => isEditable && setAmount(e.target.value)}
                                disabled={!isEditable}
                            />
                            <RiPencilLine size={16} className="text-gray-300 flex-shrink-0" />
                        </div>
                    </div>

                    <p className="text-xs font-semibold text-gray-500">
                        Not sure how much to offer?{" "}
                        <span className="text-primary">Incentive recommendations</span> will be available soon.
                    </p>
                </div>
            </div>

            {/* ── Distribution Method ───────────────────────────────────── */}
            <div className="bg-white space-y-3 rounded-xl p-3">
                <div className="space-y-1">
                    <h1 className="font-bold text-md">Incentive Distribution Method</h1>
                    <p className="text-gray-500 text-xs">
                        Choose who will handle incentive distribution after sessions are completed.
                    </p>
                </div>
                <div className="flex items-center justify-between gap-3">
                    <RadioCard
                        title="Mirats Will Automatically Distribute Incentives"
                        description="Mirats will distribute incentives to participants via their preferred digital gift card or available payout method."
                        selected={distributionMethod === "mirats_auto_distribution"}
                        onClick={() => {
                            if (!isEditable) return;
                            setDistributionMethod("mirats_auto_distribution");
                            setManualType("");
                            setManualOtherText("");
                        }}
                        disabled={!isEditable}
                    />
                    <RadioCard
                        title="Your Research Team Will Manually Distribute Incentives"
                        description="Your team will handle incentive distribution directly after the session is completed."
                        selected={distributionMethod === "client_manual_distribution"}
                        onClick={() => isEditable && setDistributionMethod("client_manual_distribution")}
                        disabled={!isEditable}
                    />
                </div>

                {/* Manual distribution sub-fields */}
                {distributionMethod === "client_manual_distribution" && (
                    <div className="space-y-3 p-3 bg-gray-50 border border-gray-100 rounded-xl">
                        {/* Gift card type */}
                        <div className="space-y-1.5">
                            <p className="text-gray-600 text-sm font-medium">
                                Gift Card Type <span className="text-red-500">*</span>
                            </p>
                            <select
                                className="border border-gray-300 rounded-md py-2.5 px-[14px] text-[#667085] text-[14px]
                                    focus:outline-none focus:ring-1 focus:ring-[#0226BE] transition-all appearance-none
                                    cursor-pointer hover:border-gray-400 bg-white w-full
                                    disabled:bg-gray-50 disabled:cursor-not-allowed"
                                style={SELECT_STYLE}
                                value={manualType ?? ""}
                                onChange={(e) => {
                                    if (!isEditable) return;
                                    setManualType(e.target.value);
                                    if (e.target.value !== "other") setManualOtherText("");
                                }}
                                disabled={!isEditable}
                            >
                                <option value="">Select the type of gift card</option>
                                {MANUAL_INCENTIVE_TYPES.map((t) => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                        </div>

                        {/* Other text */}
                        {manualType === "other" && (
                            <div className="space-y-1.5">
                                <p className="text-gray-600 text-sm font-medium">
                                    Please specify <span className="text-red-500">*</span>
                                </p>
                                <div className="border rounded-lg px-[14px] py-2.5 flex items-center gap-2 bg-white">
                                    <input
                                        type="text"
                                        className="w-full text-sm placeholder:text-sm focus:outline-none bg-transparent
                                            disabled:cursor-not-allowed"
                                        placeholder="Please specify how your team will distribute the incentive."
                                        maxLength={250}
                                        value={manualOtherText}
                                        onChange={(e) => isEditable && setManualOtherText(e.target.value)}
                                        disabled={!isEditable}
                                    />
                                    <RiPencilLine size={16} className="text-gray-300 flex-shrink-0" />
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {isEditable && (
                    <SaveBar
                        onSave={handleSave}
                        isSaving={isSaving}
                        saveSuccess={saveSuccess}
                        saveError={saveError}
                    />
                )}
            </div>
        </div>
    );
}